import { refusalOf } from "api/refusal";
import { authAxios } from "utils/axios";

/**
 * Login-session calls to the auth-service. All go through `authAxios`, which
 * sends the `access_token` / `refresh_token` cookies (`withCredentials`).
 * None of them carries a Bearer key: the session cookie IS the credential.
 */

// Logout must never hold the user on the page: a slow or dead auth-service is
// given this long, then the local session is dropped anyway.
export const LOGOUT_TIMEOUT_MS = 3000;

interface GenerateApiKeyResponse {
  status?: string;
  data?: { api_key?: string };
  api_key?: string;
}

let refreshInFlight: Promise<void> | null = null;

/**
 * Rotate the session cookies with the 3-day refresh cookie (401 when gone).
 * Rotation burns the old token, so a second concurrent refresh would read as a
 * false 401: every caller, the api-key recovery included, shares one request.
 */
export const refreshSession = (): Promise<void> => {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        await authAxios.post("/auth/refresh");
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
};

// The two `detail`s auth-service's get_current_user_id answers a missing or
// dead access cookie with. Any other 401 is a verdict on the request itself.
const NO_AUTHORIZATION_DETAIL = "No authorization";
const SESSION_EXPIRED_DETAIL_PREFIX = "Session expired";

export const isSessionFailure = (error: unknown): boolean => {
  const { status, detail } = refusalOf(error);
  return (
    status === 401 &&
    typeof detail === "string" &&
    (detail === NO_AUTHORIZATION_DETAIL || detail.startsWith(SESSION_EXPIRED_DETAIL_PREFIX))
  );
};

/**
 * Run an auth-service call; when the access cookie has lapsed (it lives 15
 * minutes, a Profile tab lives longer) refresh the session once and retry the
 * call once. Everything else passes through untouched, and so does a failed
 * refresh: the caller's error mapping reads `error.response.status`.
 */
export const withSessionRefresh = async <T>(call: () => Promise<T>): Promise<T> => {
  try {
    return await call();
  } catch (error) {
    if (!isSessionFailure(error)) throw error;
    await refreshSession();
    return call();
  }
};

/** End the server-side session and delete its cookies. */
export const logoutSession = async (): Promise<void> => {
  await authAxios.post("/auth/logout", undefined, {
    timeout: LOGOUT_TIMEOUT_MS,
  });
};

/**
 * Return the signed-in account's CURRENT api key (the auth-service returns the
 * existing key, or creates one if the account has none). 401 when the access
 * cookie is missing or expired.
 */
export const generateApiKey = async (): Promise<string> => {
  const response = await authAxios.post<GenerateApiKeyResponse>(
    "/auth/generate_api_key"
  );
  const key = response.data?.data?.api_key ?? response.data?.api_key;
  if (!key) {
    throw new Error("generate_api_key returned no api_key");
  }
  return key;
};
